/**
 * SP4-Nachtrag — welche Nachrichten eines Konversations-Threads warten noch auf
 * ihren Body?
 *
 * Hintergrund: `fetchConversation` liefert die Nachrichten eines Threads OHNE
 * Body (`mapItem` ohne `detail` setzt `body: ""`) — jeder Abschnitt muss seinen
 * Text einzeln über `fetchMessageBody` nachladen. Das Aufklappen einer Karte
 * stößt diesen Fetch an; das AUTOMATISCHE Aufklappen der neuesten Nachricht tat
 * es nicht. War das nicht zufällig die geöffnete Mail — häufig, denn die
 * neueste Nachricht eines Threads ist oft die eigene gesendete Antwort, die gar
 * nicht in der Liste stand —, blieb die Karte dauerhaft im Spinner, bis der
 * Nutzer sie von Hand zu- und wieder aufklappte.
 *
 * Diese Funktion beantwortet die Frage rein rechnerisch (und damit testbar):
 * offen, aber weder Body geladen noch Fehler gemeldet ⇒ nachladen.
 *
 * Reihenfolge und Duplikat-Freiheit folgen `nachrichtenIds`, damit der Aufrufer
 * die Fetches in Thread-Reihenfolge auslöst.
 */
export function bodiesNachzuladen(
    nachrichtenIds: readonly string[],
    offeneIds: ReadonlySet<string>,
    geladeneBodies: Readonly<Record<string, unknown>>,
    fehlgeschlageneIds: ReadonlySet<string>,
): string[] {
    const gesehen = new Set<string>();
    const offen: string[] = [];
    for (const id of nachrichtenIds) {
        if (!id || gesehen.has(id)) continue;
        gesehen.add(id);
        if (!offeneIds.has(id)) continue;
        // Body schon da → nichts zu tun. `undefined` als Wert zählt als „fehlt".
        if (geladeneBodies[id] != null) continue;
        // Fehlgeschlagene zeigen ihre eigene Fehlerzeile mit „Neu laden" — kein
        // stiller Dauer-Retry.
        if (fehlgeschlageneIds.has(id)) continue;
        offen.push(id);
    }
    return offen;
}

/**
 * Outlook Desktop — die Nachrichten einer Unterhaltung im Lesebereich: neueste
 * OBEN. `fetchConversation` liefert älteste zuerst (Outlook Web); hier wird für
 * die Anzeige umgedreht, ohne die Eingabe zu verändern. Bei gleicher Zeit
 * bleibt die gelieferte Reihenfolge rückwärts erhalten.
 */
export function neuesteZuerst<T extends { date: Date }>(nachrichten: readonly T[]): T[] {
    return nachrichten
        .map((n, i) => ({ n, i }))
        .sort((a, b) => b.n.date.getTime() - a.n.date.getTime() || b.i - a.i)
        .map(({ n }) => n);
}
